<template>
  <div class="pio-mask" @click.self="close">
    <img
      ref="img"
      :src="src"
      class="pio-img"
      :style="imgStyle"
      @click.stop="onTap($event)"
      @touchstart.stop="onTouchStart"
      @touchmove.stop.prevent="onTouchMove"
      @touchend.stop="onTouchEnd"
      @mousedown.stop="onMouseDown"
    />
    <div class="pio-close" @click.stop="close">
      <i class="el-icon-close"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PreviewImageOverlay',
  props: {
    src: { type: String, required: true }
  },
  data() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      minScale: 1,
      maxScale: 15, //允许更大缩放 
      isPanning: false,
      lastX: 0,
      lastY: 0,
      startX: 0,
      startY: 0,
      // velocity tracking
      velX: 0,
      velY: 0,
      lastMoveTs: 0,
      inertiaFrame: null,
      // pinch
      pinchStartDist: 0,
      pinchStartScale: 1,
      viewportW: 0,
      viewportH: 0,
      imgAspect: 1,
      imgNaturalW: 0,
      imgNaturalH: 0,
      imgBaseW: 0, // 图片在 1x 缩放时的实际显示宽度
      imgBaseH: 0, // 图片在 1x 缩放时的实际显示高度
      lastTapTime: 0,
      tapTimer: null,
      tapScaleSnapshot: 1,
      // zoom animation
      zoomAnimFrame: null,
      zoomAnimStart: 0,
      zoomFrom: null,
      zoomTo: null,
      // snap-back animation
      snapFrame: null
    }
  },
  computed: {
    imgStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
        transition: 'transform 0s',
        maxHeight: '100vh',
        maxWidth: '100vw'
      }
    }
  },
  mounted() {
    this.viewportW = window.innerWidth || document.documentElement.clientWidth
    this.viewportH = window.innerHeight || document.documentElement.clientHeight
    const tmp = new Image()
    tmp.onload = () => {
      this.imgNaturalW = tmp.naturalWidth || 0
      this.imgNaturalH = tmp.naturalHeight || 0
      this.imgAspect = this.imgNaturalW && this.imgNaturalH ? this.imgNaturalW / this.imgNaturalH : 1
      // 计算图片在 1x 时的实际显示尺寸（考虑 CSS max-width/max-height 限制）
      const naturalAspect = this.imgAspect
      const viewportAspect = this.viewportW / this.viewportH
      
      if (naturalAspect > viewportAspect) {
        // 图片更宽，以宽度为准
        this.imgBaseW = this.viewportW
        this.imgBaseH = this.viewportW / naturalAspect
      } else {
        // 图片更高，以高度为准
        this.imgBaseW = this.viewportH * naturalAspect
        this.imgBaseH = this.viewportH
      }
      
      // 如果图片自然尺寸小于视口，使用自然尺寸
      if (this.imgNaturalW < this.viewportW && this.imgNaturalH < this.viewportH) {
        this.imgBaseW = this.imgNaturalW
        this.imgBaseH = this.imgNaturalH
      }
      
      // 等待图片元素渲染完成后，验证并更新尺寸
      this.$nextTick(() => {
        const imgEl = this.$refs.img
        if (imgEl) {
          // 使用实际渲染尺寸作为参考（但优先使用计算值，因为更准确）
          const actualW = imgEl.clientWidth || 0
          const actualH = imgEl.clientHeight || 0
          // 如果计算值和实际值差异较大，使用实际值（容差 5px）
          if (Math.abs(actualW - this.imgBaseW) > 5) {
            this.imgBaseW = actualW
          }
          if (Math.abs(actualH - this.imgBaseH) > 5) {
            this.imgBaseH = actualH
          }
        }
        this.clamp()
      })
    }
    tmp.src = this.src
    window.addEventListener('wheel', this.onWheel, { passive: false })
    window.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('mousemove', this.onMouseMove)
  },
  beforeDestroy() {
    window.removeEventListener('wheel', this.onWheel)
    window.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('mousemove', this.onMouseMove)
    if (this.inertiaFrame) cancelAnimationFrame(this.inertiaFrame)
    if (this.zoomAnimFrame) cancelAnimationFrame(this.zoomAnimFrame)
    if (this.snapFrame) cancelAnimationFrame(this.snapFrame)
  },
  methods: {
    close() {
      this.$emit('close')
    },
    // 更顺滑的缓出曲线
    easeOutQuint(t) { return 1 - Math.pow(1 - t, 5) },
    animateZoomAt(cx, cy, targetScale, duration = 260) {
      if (this.zoomAnimFrame) cancelAnimationFrame(this.zoomAnimFrame)
      const start = performance.now()
      const from = { scale: this.scale, x: this.translateX, y: this.translateY }
      const vpW = this.viewportW
      const vpH = this.viewportH
      const pX = cx - vpW / 2
      const pY = cy - vpH / 2
      const ratio = targetScale / this.scale
      const to = {
        scale: targetScale,
        x: pX * (1 - ratio) + this.translateX * ratio,
        y: pY * (1 - ratio) + this.translateY * ratio
      }
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration)
        const k = this.easeOutQuint(t)
        this.scale = from.scale + (to.scale - from.scale) * k
        this.translateX = from.x + (to.x - from.x) * k
        this.translateY = from.y + (to.y - from.y) * k
        // 在动画过程中，如果图片高度未超过视口，强制 translateY = 0，避免回弹
        const { maxY } = this.computeBounds()
        if (maxY === 0) {
          this.translateY = 0
        } else {
          // 只有当图片高度超过视口时，才应用 clamp 限制
          this.clamp()
        }
        if (t < 1) {
          this.zoomAnimFrame = requestAnimationFrame(step)
        } else {
          this.zoomAnimFrame = null
          // 动画结束后，检查是否需要回弹或回到中心
          const finalBounds = this.computeBounds()
          // 如果缩小到 scale=1 且图片未超过视口，使用 snapBack 平滑回到中心
          if (targetScale <= 1.05 && finalBounds.maxX === 0 && finalBounds.maxY === 0) {
            // 图片未超过视口，如果不在中心则平滑回到中心
            if (Math.abs(this.translateX) > 0.5 || Math.abs(this.translateY) > 0.5) {
              this.startSnapBack(200)
            }
          } else if (finalBounds.maxY > 0) {
            // 图片高度超过视口，检查是否需要回弹
            const overX = Math.abs(this.translateX) > finalBounds.maxX
            const overY = Math.abs(this.translateY) > finalBounds.maxY
            if (overX || overY) {
              this.startSnapBack(200)
            }
          } else if (finalBounds.maxX > 0) {
            // 只有宽度超过视口，检查是否需要回弹
            const overX = Math.abs(this.translateX) > finalBounds.maxX
            if (overX) {
              this.startSnapBack(200)
            }
          }
        }
      }
      this.zoomAnimFrame = requestAnimationFrame(step)
    },
    computeBounds() {
      // 使用保存的基础尺寸（1x 时的实际显示尺寸）乘以当前 scale 来计算实际尺寸
      let baseW = this.imgBaseW || 0
      let baseH = this.imgBaseH || 0
      
      // 如果基础尺寸未获取到，降级使用图片元素的自然尺寸
      if (!baseW || !baseH) {
        const imgEl = this.$refs.img
        if (imgEl) {
          // 获取图片元素的自然尺寸（不受 transform 影响）
          const naturalW = imgEl.naturalWidth || 0
          const naturalH = imgEl.naturalHeight || 0
          if (naturalW && naturalH) {
            // 计算在 1x scale 时，图片在视口中的实际显示尺寸
            const naturalAspect = naturalW / naturalH
            const viewportAspect = this.viewportW / this.viewportH
            
            if (naturalAspect > viewportAspect) {
              // 图片更宽，以宽度为准
              baseW = Math.min(naturalW, this.viewportW)
              baseH = baseW / naturalAspect
            } else {
              // 图片更高，以高度为准
              baseH = Math.min(naturalH, this.viewportH)
              baseW = baseH * naturalAspect
            }
          }
        }
      }
      
      // 计算当前 scale 下的实际尺寸
      const imgW = baseW * this.scale
      const imgH = baseH * this.scale
      
      // 当图片高度不超过视口时，强制 maxY = 0（不允许上下弹性）
      // 使用严格的小于等于判断，避免浮点数误差（容差 0.5px）
      const maxY = imgH <= this.viewportH + 0.5 ? 0 : Math.max(0, (imgH - this.viewportH) / 2)
      
      return {
        maxX: Math.max(0, (imgW - this.viewportW) / 2),
        maxY: maxY
      }
    },
    // 减小阻尼，拉过头更多，手感更“软”
    applyResistance(val, max) {
      if (max <= 0) return 0 // 不允许该方向有弹性
      if (Math.abs(val) <= max) return val
      const sign = val >= 0 ? 1 : -1
      const overflow = val - sign * max
      return sign * max + overflow * 0.2
    },
    startSnapBack(duration = 200) {
      if (this.snapFrame) cancelAnimationFrame(this.snapFrame)
      const { maxX, maxY } = this.computeBounds()
      const from = { x: this.translateX, y: this.translateY }
      const to = {
        x: Math.max(-maxX, Math.min(maxX, this.translateX)),
        y: Math.max(-maxY, Math.min(maxY, this.translateY))
      }
      if (from.x === to.x && from.y === to.y) return
      // 立即开始，无延迟
      const start = performance.now()
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration)
        const k = this.easeOutQuint(t)
        this.translateX = from.x + (to.x - from.x) * k
        this.translateY = from.y + (to.y - from.y) * k
        if (t < 1) {
          this.snapFrame = requestAnimationFrame(step)
        } else {
          this.snapFrame = null
        }
      }
      // 立即执行第一步，不等待下一帧
      step(performance.now())
    },
    startInertia() {
      if (this.inertiaFrame) cancelAnimationFrame(this.inertiaFrame)
      const friction = 0.96 // 更长更顺滑
      const minV = 0.05     // 更低阈值
      const step = () => {
        if (this.scale <= 1) return
        this.translateX += this.velX
        this.translateY += this.velY
        const { maxX, maxY } = this.computeBounds()
        const overX = Math.abs(this.translateX) > maxX
        // 只有当图片高度超过视口时，才检查上下越界
        const overY = maxY > 0 && Math.abs(this.translateY) > maxY
        if (overX) this.velX *= 0.88
        if (overY) this.velY *= 0.88
        this.velX *= friction
        this.velY *= friction
        if (Math.abs(this.velX) < minV && Math.abs(this.velY) < minV) {
          this.inertiaFrame = null
          // 只在真正越界时才回弹
          if (overX || overY) {
            this.startSnapBack(200)
          }
          return
        }
        this.inertiaFrame = requestAnimationFrame(step)
      }
      this.inertiaFrame = requestAnimationFrame(step)
    },
    onTap(e) {
      const now = Date.now()
      const diff = now - this.lastTapTime
      this.lastTapTime = now
      const isDouble = (e && e.detail >= 2) || diff < 350
      if (isDouble) {
        const imgEl = this.$refs.img
        const h0 = imgEl ? (imgEl.clientHeight || 1) : 1
        if (this.scale <= 1.05) {
          // 放大到页面高度的95%，避免超过页面高度产生上下弹性
          const target = Math.min(this.maxScale, Math.max(this.minScale, this.scale * (this.viewportH * 0.95 / h0)))
          this.animateZoomAt(e.clientX, e.clientY, target)
        } else {
          // 缩小到 scale=1，animateZoomAt 会自动处理回到中心位置的逻辑
          this.animateZoomAt(e.clientX, e.clientY, 1)
        }
        if (this.tapTimer) { clearTimeout(this.tapTimer); this.tapTimer = null }
        return
      }
      if (this.tapTimer) clearTimeout(this.tapTimer)
      this.tapTimer = setTimeout(() => {
        this.close()
        this.tapTimer = null
      }, 300)
    },
    onWheel(e) {
      if (!this.$el) return
      e.preventDefault()
      const delta = -e.deltaY
      const factor = delta > 0 ? 1.1 : 0.9
      const target = Math.min(this.maxScale, Math.max(this.minScale, this.scale * factor))
      this.animateZoomAt(e.clientX, e.clientY, target, 160)
    },
    zoomAt(cx, cy, targetScale) {
      const old = this.scale
      const next = Math.max(this.minScale, Math.min(this.maxScale, targetScale))
      const vpW = this.viewportW
      const vpH = this.viewportH
      const pX = cx - vpW / 2
      const pY = cy - vpH / 2
      const ratio = next / old
      this.translateX = pX * (1 - ratio) + this.translateX * ratio
      this.translateY = pY * (1 - ratio) + this.translateY * ratio
      this.scale = next
      // 在缩放后，如果图片高度未超过视口，强制 translateY = 0
      const { maxY } = this.computeBounds()
      if (maxY === 0) {
        this.translateY = 0
      } else {
        this.clamp()
      }
    },
    clamp() {
      const { maxX, maxY } = this.computeBounds()
      if (this.translateX > maxX) this.translateX = maxX
      if (this.translateX < -maxX) this.translateX = -maxX
      // 当图片高度未超过视口时，强制 translateY = 0
      if (maxY === 0) {
        this.translateY = 0
      } else {
        if (this.translateY > maxY) this.translateY = maxY
        if (this.translateY < -maxY) this.translateY = -maxY
      }
    },
    onTouchStart(e) {
      if (e.touches.length === 2) {
        const [a, b] = e.touches
        this.pinchStartDist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
        this.pinchStartScale = this.scale
        if (this.inertiaFrame) cancelAnimationFrame(this.inertiaFrame)
        return
      }
      const t = e.touches[0]
      this.isPanning = true
      this.startX = t.clientX - this.translateX
      this.startY = t.clientY - this.translateY
      this.lastX = t.clientX
      this.lastY = t.clientY
      this.lastMoveTs = performance.now()
      this.velX = 0
      this.velY = 0
      if (this.inertiaFrame) cancelAnimationFrame(this.inertiaFrame)
    },
    onTouchMove(e) {
      if (e.touches.length === 2) {
        const [a, b] = e.touches
        const midX = (a.clientX + b.clientX) / 2
        const midY = (a.clientY + b.clientY) / 2
        const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
        const ratio = this.pinchStartDist ? (dist / this.pinchStartDist) : 1
        const next = Math.max(this.minScale, Math.min(this.pinchStartScale * ratio, this.maxScale))
        this.zoomAt(midX, midY, next)
        return
      }
      if (!this.isPanning || this.scale === 1) return
      const t = e.touches[0]
      const now = performance.now()
      const dt = Math.max(1, now - this.lastMoveTs)
      const dx = t.clientX - this.lastX
      const dy = t.clientY - this.lastY
      // 拖动时允许自由移动，不应用阻力，只在松手时回弹
      this.translateX += dx
      // 只有当图片高度超过视口时，才允许上下拖动
      const { maxY } = this.computeBounds()
      if (maxY > 0) {
        this.translateY += dy
        this.velY = dy / dt * 16
      } else {
        // 图片高度未超过视口时，强制 translateY = 0，不允许上下拖动
        this.translateY = 0
        this.velY = 0
      }
      this.velX = dx / dt * 16
      this.lastX = t.clientX
      this.lastY = t.clientY
      this.lastMoveTs = now
    },
    onTouchEnd() {
      this.isPanning = false
      if (this.scale > 1 && (Math.abs(this.velX) > 0.3 || Math.abs(this.velY) > 0.3)) {
        this.startInertia()
      } else {
        // 只在真正越界时才回弹，立即开始，无延迟
        const { maxX, maxY } = this.computeBounds()
        const overX = Math.abs(this.translateX) > maxX
        // 只有当图片高度超过视口时，才检查上下越界
        const overY = maxY > 0 && Math.abs(this.translateY) > maxY
        if (overX || overY) {
          this.startSnapBack(200)
        }
      }
    },
    onMouseDown(e) {
      if (this.scale === 1) return
      this.isPanning = true
      this.startX = e.clientX - this.translateX
      this.startY = e.clientY - this.translateY
      this.lastX = e.clientX
      this.lastY = e.clientY
      this.lastMoveTs = performance.now()
      this.velX = 0
      this.velY = 0
      if (this.inertiaFrame) cancelAnimationFrame(this.inertiaFrame)
    },
    onMouseMove(e) {
      if (!this.isPanning) return
      const now = performance.now()
      const dt = Math.max(1, now - this.lastMoveTs)
      const dx = e.clientX - this.lastX
      const dy = e.clientY - this.lastY
      // 拖动时允许自由移动，不应用阻力，只在松手时回弹
      this.translateX += dx
      // 只有当图片高度超过视口时，才允许上下拖动
      const { maxY } = this.computeBounds()
      if (maxY > 0) {
        this.translateY += dy
        this.velY = dy / dt * 16
      } else {
        // 图片高度未超过视口时，强制 translateY = 0，不允许上下拖动
        this.translateY = 0
        this.velY = 0
      }
      this.velX = dx / dt * 16
      this.lastX = e.clientX
      this.lastY = e.clientY
      this.lastMoveTs = now
    },
    onMouseUp() {
      this.isPanning = false
      if (this.scale > 1 && (Math.abs(this.velX) > 0.3 || Math.abs(this.velY) > 0.3)) {
        this.startInertia()
      } else {
        // 只在真正越界时才回弹，立即开始，无延迟
        const { maxX, maxY } = this.computeBounds()
        const overX = Math.abs(this.translateX) > maxX
        // 只有当图片高度超过视口时，才检查上下越界
        const overY = maxY > 0 && Math.abs(this.translateY) > maxY
        if (overX || overY) {
          this.startSnapBack(200)
        }
      }
    }
  }
}
</script>

<style scoped>
.pio-mask {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pio-img {
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  pointer-events: auto;
  will-change: transform;
  transform-origin: center center;
  max-width: none;
  touch-action: none; /* 避免浏览器默认双击缩放/手势干扰 */
}
.pio-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.pio-close i {
  color: #fff;
  font-size: 20px;
}
</style>
